import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPatch } from '../../../api/client';
import { Button } from '../../../components/ui/Button';
import { Input, Select } from '../../../components/ui/Input';
import { Section } from '../../../components/ui/Section';

interface CalendarSettingsForm {
  timezone: string;
  workDays: number[];
  eventBufferMinutes: number;
  defaultEventDurationHours: number | null;
  autoSyncGoogle: boolean;
}

const TIMEZONE_OPTIONS = [
  { value: 'Europe/Rome', label: 'Europa/Roma (CET/CEST)' },
  { value: 'Europe/London', label: 'Europa/Londra (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europa/Parigi (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Europa/Berlino (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Europa/Madrid (CET/CEST)' },
  { value: 'America/New_York', label: 'America/New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (PST/PDT)' },
  { value: 'UTC', label: 'UTC' },
];

const DAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

export default function CalendarSettingsSection({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CalendarSettingsForm>({
    timezone: 'Europe/Rome', workDays: [1, 2, 3, 4, 5], eventBufferMinutes: 60, defaultEventDurationHours: null, autoSyncGoogle: true,
  });
  const [loaded, setLoaded] = useState(false);

  const { data } = useQuery<CalendarSettingsForm>({
    queryKey: ['user-settings-calendar'],
    queryFn: () => apiGet<CalendarSettingsForm>('/user-settings/calendar-settings'),
  });

  useEffect(() => {
    if (data && !loaded) {
      setForm({
        timezone: data.timezone ?? 'Europe/Rome',
        workDays: data.workDays ?? [1, 2, 3, 4, 5],
        eventBufferMinutes: data.eventBufferMinutes ?? 60,
        defaultEventDurationHours: data.defaultEventDurationHours ?? null,
        autoSyncGoogle: data.autoSyncGoogle ?? true,
      });
      setLoaded(true);
    }
  }, [data, loaded]);

  const saveMutation = useMutation({
    mutationFn: () => apiPatch('/user-settings/calendar-settings', {
      timezone: form.timezone || null,
      workDays: form.workDays,
      eventBufferMinutes: form.eventBufferMinutes,
      defaultEventDurationHours: form.defaultEventDurationHours,
      autoSyncGoogle: form.autoSyncGoogle,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-settings-calendar'] });
      toast.success('Impostazioni calendario salvate');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleDay = (day: number) => {
    setForm(f => ({
      ...f,
      workDays: f.workDays.includes(day) ? f.workDays.filter(d => d !== day) : [...f.workDays, day].sort(),
    }));
  };

  return (
    <Section title="Impostazioni calendario">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Select
          label="Fuso orario"
          value={form.timezone}
          onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}
          options={TIMEZONE_OPTIONS}
        />

        <div>
          <label style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.5rem' }}>
            Giorni lavorativi
          </label>
          <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
            {DAY_LABELS.map((label, i) => {
              const active = form.workDays.includes(i);
              return (
                <button
                  key={i}
                  onClick={() => toggleDay(i)}
                  style={{
                    padding: '0.375rem 0.75rem',
                    background: active ? 'var(--color-primary-alpha)' : 'var(--glass-bg-card)',
                    border: `1px solid ${active ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
                    borderRadius: 'var(--radius-card)',
                    color: active ? 'var(--color-primary)' : 'var(--color-text)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-sm)',
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <Input
          label="Buffer minimo tra eventi (minuti)"
          type="number"
          min={0}
          max={480}
          value={String(form.eventBufferMinutes)}
          onChange={e => setForm(f => ({ ...f, eventBufferMinutes: Math.max(0, Math.min(480, parseInt(e.target.value) || 0)) }))}
          hint="Tempo minimo tra la fine di un evento e l'inizio del successivo"
          style={{ maxWidth: 200 }}
        />

        <Input
          label="Durata default eventi (ore) per Google Calendar"
          type="number"
          min={1}
          max={24}
          placeholder="4"
          value={form.defaultEventDurationHours != null ? String(form.defaultEventDurationHours) : ''}
          onChange={e => {
            const v = e.target.value;
            setForm(f => ({ ...f, defaultEventDurationHours: v === '' ? null : Math.max(1, Math.min(24, parseInt(v) || 1)) }));
          }}
          hint="Se non imposti un orario di fine, l'evento durerà queste ore dopo l'inizio. Lascia vuoto per il default (4 ore)."
          style={{ maxWidth: 200 }}
        />

        {isAdmin && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.autoSyncGoogle}
            onChange={e => setForm(f => ({ ...f, autoSyncGoogle: e.target.checked }))}
          />
          Sincronizza automaticamente nuovi eventi con Google Calendar
        </label>
        )}

        <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
          Salva impostazioni calendario
        </Button>
      </div>
    </Section>
  );
}
